import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCompany, updateCompany, registerCompany, type UpdateCompanyError, type RegisterCompanyError, type CompanyRegistrationRequest, fetchPossibleCompanyLevels } from '../api';
import type { Company } from '../models';
import { companiesKeys } from '@/features/companies/hooks';
import { localizedReferenceDataKeys } from '@/locales/localizedReferenceData';
import { useAppLocale } from '@/locales/useAppLocale';

export const companyKeys = {
  all: ['company'] as const,
  detail: (uuid: string) => [...companyKeys.all, uuid] as const,
  possibleLevels: () => localizedReferenceDataKeys.parentAssignableLevels,
  possibleLevelsList: (uuid: string, locale: string) =>
    [...companyKeys.possibleLevels(), uuid, locale] as const,
};

export function useCompany(companyUuid: string) {
  return useQuery({
    queryKey: [...companyKeys.detail(companyUuid)],
    queryFn: () => fetchCompany(companyUuid),
  });
}

export function useCompanyPossibleLevels(companyUuid: string, enabled = true) {
  const locale = useAppLocale();

  return useQuery({
    queryKey: companyKeys.possibleLevelsList(companyUuid, locale),
    queryFn: () =>
      fetchPossibleCompanyLevels(companyUuid, locale).then(
        (company) => company.possibleLevels ?? []
      ),
    enabled: enabled && !!companyUuid,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation<Company, UpdateCompanyError, Company>({
    mutationFn: updateCompany,
    onSuccess: (data) => {
      queryClient.setQueryData(companyKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
    },
  });
}

export function useRegisterCompany() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; company_id?: string }, RegisterCompanyError, CompanyRegistrationRequest>({
    mutationFn: registerCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
    },
  });
}
